/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { logger } from '@touchplan/logging';
import { IPoint } from 'pixi.js-legacy';

/**
 * We were missing an interface for events that emit from Hammer and our EventBinder, so that's what this is for.
 */
export interface HammerEvent {
	/**
	 * Additional event type (e.g., "panleft", "panright", "taphold")
	 * */
	additionalEvent?: string;
	/**
	 * Angle of the gesture in degrees
	 *
	 */
	angle?: number;
	/**
	 * Center point of the gesture
	 */
	center?: IPoint;
	/**
	 * Array of changed pointer events
	 */
	changedPointers?: PointerEvent[];
	/**
	 * Time elapsed since the gesture started (in milliseconds)
	 */
	deltaTime?: number;
	/**
	 * Horizontal distance moved since gesture start
	 */
	deltaX?: number;
	/**
	 * Vertical distance moved since gesture start
	 */
	deltaY?: number;
	/**
	 * Direction of the gesture (Hammer.js direction constants)
	 */
	direction?: number;
	/**
	 * Total distance moved during the gesture
	 */
	distance?: number;
	/**
	 * Type of event (Hammer.js event type constants)
	 */
	eventType?: number;
	/**
	 * Whether this is the final event in the gesture sequence
	 */
	isFinal?: boolean;
	/**
	 * Whether this is the first event in the gesture sequence
	 */
	isFirst?: boolean;
	/**
	 * Maximum number of pointers used during the gesture
	 */
	maxPointers?: number;
	/**
	 * Direction of movement from the last event
	 */
	offsetDirection?: number;
	/**
	 * Overall velocity of the gesture (pixels per millisecond)
	 */
	overallVelocity?: number;
	/**
	 * Overall horizontal velocity of the gesture
	 */
	overallVelocityX?: number;
	/**
	 * Overall vertical velocity of the gesture
	 */
	overallVelocityY?: number;
	/**
	 * Type of pointer used ("mouse", "touch", "pen")
	 */
	pointerType?: string;
	/**
	 * Array of all active pointers
	 */
	pointers?: PointerEvent[];
	/**
	 * Function to prevent default browser behavior
	 */
	preventDefault?: () => void;
	/**
	 * Rotation angle during pinch/rotate gestures
	 */
	rotation?: number;
	/**
	 * Scale factor during pinch gesture
	 */
	scale?: number;

	/**
	 * Original browser event that triggered this gesture
	 */
	srcEvent?: PointerEvent;
	/**
	 * The DOM element that was the target of the event
	 */
	target?: HTMLElement;
	/**
	 * Timestamp when the event occurred
	 */
	timeStamp?: number;
	/**
	 * Primary gesture type ("pan", "tap", "swipe", "pinch", "rotate")
	 */
	type?: string;
	/**
	 * Current velocity of the gesture
	 */
	velocity?: number;
	/**
	 * Current horizontal velocity
	 */
	velocityX?: number;
	/**
	 * Current vertical velocity
	 */
	velocityY?: number;
}

//reduce some boilerplate...
export class EventBinder {
	constructor(private parent, private startEvent?: string | string[], private endEvent?: string | string[]) {
		if (startEvent) {
			if (typeof startEvent === 'string') {
				this.startEvents[startEvent] = startEvent;
			} else {
				startEvent.forEach((se) => (this.startEvents[se] = se));
			}
		}
		if (endEvent) {
			if (typeof endEvent === 'string') {
				this.endEvents[endEvent] = endEvent;
			} else {
				endEvent.forEach((se) => (this.endEvents[se] = se));
			}
		}
	}
	private destroyList = new Map<string, any>();
	private failed = false;
	public detecting = false;

	private startEvents: any = {};
	private endEvents: any = {};
	/**
	 * add a callback
	 * @param  eventName name of the event to added
	 * @param  cb        The handler, if this returns a truthy value in the start
	 * event all subsequent events will be cancelled until the start event is called again.
	 * This check is only run if a startEvent is passed into the constructor.
	 */
	public add(eventName: string, cb) {
		this.parent.on(eventName, (event) => {
			if (this.startEvents[eventName]) {
				this.detecting = true;
				this.failed = !!cb(event);
				if (this.failed) {
					this.detecting = false;
				}
			} else if (this.endEvents[eventName]) {
				if (!this.failed) {
					cb(event);
				}
				this.detecting = false;
			} else if (!this.failed) {
				const ret = cb(event);
				if (ret) {
					this.failed = true;
					this.detecting = false;
				}
			}
			//the last case is that it's failed and it's not the start event
		});

		if (this.destroyList.has(eventName)) {
			logger.log('[EventBinder] overwriting ' + eventName + ' this is probably a bug');
		}
		this.destroyList.set(eventName, cb);
	}
	public destroy() {
		this.destroyList.forEach((cb, key) => {
			this.parent.removeListener(key, cb);
		});
		this.destroyList = null;
		this.parent = null;
	}
}
